import React, { useState } from 'react'
import MaterialTable from '@material-table/core'
import { TableCell } from '@mui/material'
import * as UIcons from '@iconscout/react-unicons'
import { withTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import useSWR from 'swr'
import ActionsMenu from '../../shared/ActionsMenu'
import StatusPill from '../../shared/StatusPill'
import EmptyUsers from './EmptyUsers'

export default withTranslation()(function UserTable({ t, q = '', branch = '', status = '', onViewDetail, onEdit, onAssignRole, onResetPassword, onToggleStatus }) {
  const { data } = useSWR('user-mgmt/users', { revalidateOnFocus: true })
  const { data: branchesRes } = useSWR('merchants/branches')
  const [selectedCount, setSelectedCount] = useState(0)

  const users = data?.data?.data ?? []
  const branches = branchesRes?.data ?? []
  const branchNameById = branches.reduce((acc, item) => ({ ...acc, [item.id]: item.name }), {})

  const isActive = (user) => (user.status ?? 'active') !== 'inactive' && user.status !== 'suspended'

  const filtered = users.filter((user) => {
    const query = q.trim().toLowerCase()
    if (query && !`${user.first_name} ${user.last_name}`.toLowerCase().includes(query)) return false
    if (branch && `${user.branch_id}` !== `${branch}`) return false
    if (status && (isActive(user) ? 'active' : 'inactive') !== status) return false
    return true
  })

  const copyEmail = (email) => {
    navigator.clipboard.writeText(email)
    toast.success(t('settings.user_management.copied'))
  }

  if (!users.length) return <EmptyUsers />

  return (
    <div className="w-full bg-white rounded-xl border border-border-lines overflow-hidden">
      <MaterialTable
        columns={[
          {
            title: t('settings.user_management.columns.user'),
            field: 'first_name',
            render: (row) => (
              <div>
                <p className="text-dark-blue font-medium">{`${row.first_name} ${row.last_name}`}</p>
                <p className="text-xs text-secondary">{row.roles?.[0]?.display_name ?? '—'}</p>
              </div>
            )
          },
          {
            title: t('settings.user_management.columns.email'),
            field: 'email',
            render: (row) => (
              <div className="flex items-center gap-2">
                {row.email}
                <button type="button" onClick={() => copyEmail(row.email)} className="text-secondary hover:text-primary">
                  <UIcons.UilCopy size={14} />
                </button>
              </div>
            )
          },
          { title: t('settings.user_management.columns.branch'), field: 'branch_id', render: (row) => branchNameById[row.branch_id] ?? '—' },
          { title: t('settings.user_management.columns.phone'), field: 'phone' },
          { title: t('misc.status'), field: 'status', render: (row) => <StatusPill active={isActive(row)} /> }
        ]}
        data={filtered}
        actions={[{ position: 'row', tooltip: t('settings.user_management.view_details') }]}
        onSelectionChange={(rows) => setSelectedCount(rows.length)}
        options={{
          selection: true,
          actionsColumnIndex: -1,
          toolbar: false,
          pageSize: 10,
          pageSizeOptions: [10, 20, 50],
          headerStyle: { textTransform: 'uppercase', fontSize: '12px', color: '#748799', backgroundColor: '#F5F6FA' }
        }}
        localization={{
          header: { actions: '' },
          body: { emptyDataSourceMessage: t('misc.no_records') }
        }}
        components={{
          Pagination: (props) => {
            const totalPages = Math.max(1, Math.ceil(props.count / props.rowsPerPage))
            const currentPage = props.page + 1

            return (
              <TableCell colSpan={props.colSpan}>
                <div className="flex flex-wrap items-center justify-between gap-3 py-1">
                  <span className="text-sm text-secondary">
                    {t('settings.user_management.table.selected', { count: selectedCount, total: props.count })}
                  </span>

                  <div className="flex items-center gap-4">
                    <select
                      value={props.rowsPerPage}
                      onChange={(event) => props.onRowsPerPageChange(event)}
                      className="border border-border-lines rounded-lg px-2 py-1 text-sm text-dark-blue"
                    >
                      {(props.rowsPerPageOptions ?? [10, 20, 50]).map((size) => <option key={size} value={size}>{size}</option>)}
                    </select>

                    <span className="text-sm text-secondary whitespace-nowrap">
                      {t('settings.user_management.table.page_of', { page: currentPage, total: totalPages })}
                    </span>

                    <div className="flex items-center gap-1">
                      <button type="button" onClick={(event) => props.onPageChange(event, 0)} disabled={props.page === 0} className="p-1 rounded disabled:opacity-30">
                        <UIcons.UilAngleDoubleLeft size={16} className="text-secondary" />
                      </button>
                      <button type="button" onClick={(event) => props.onPageChange(event, props.page - 1)} disabled={props.page === 0} className="p-1 rounded disabled:opacity-30">
                        <UIcons.UilAngleLeft size={16} className="text-secondary" />
                      </button>
                      <button type="button" onClick={(event) => props.onPageChange(event, props.page + 1)} disabled={currentPage >= totalPages} className="p-1 rounded disabled:opacity-30">
                        <UIcons.UilAngleRight size={16} className="text-secondary" />
                      </button>
                      <button type="button" onClick={(event) => props.onPageChange(event, totalPages - 1)} disabled={currentPage >= totalPages} className="p-1 rounded disabled:opacity-30">
                        <UIcons.UilAngleDoubleRight size={16} className="text-secondary" />
                      </button>
                    </div>
                  </div>
                </div>
              </TableCell>
            )
          },

          Action: ({ data: row }) => (
            <ActionsMenu
              items={[
                { key: 'view', icon: UIcons.UilInfoCircle, label: t('settings.user_management.view_details'), onClick: () => onViewDetail(row) },
                { key: 'edit', icon: UIcons.UilEditAlt, label: t('settings.user_management.edit_user'), onClick: () => onEdit(row) },
                { key: 'assign-role', icon: UIcons.UilUserPlus, label: t('settings.user_management.assign_role'), onClick: () => onAssignRole(row) },
                { key: 'reset-password', icon: UIcons.UilLock, label: t('settings.user_management.reset_password'), onClick: () => onResetPassword(row) },
                {
                  key: 'toggle-status',
                  icon: UIcons.UilBan,
                  label: t(isActive(row) ? 'settings.user_management.deactivate' : 'settings.user_management.enable'),
                  danger: isActive(row),
                  onClick: () => onToggleStatus(row)
                }
              ]}
            />
          )
        }}
      />
    </div>
  )
})
